import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { BG_GRAY, LIGHT_GRAY } from '../../common/colors';

// Product screenshot beside a short list of tool-specific highlights. Used in
// place of the generic featureList on tools that have a screenshot worth
// showing. The image is delivered as a bare viewport capture at 1.4:1 — the
// rounded border and elevation are drawn here so they match the rest of the
// site rather than being baked into the PNG.
const Highlight = ({ title, text }) => (
  <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 2.5 }}>
    <Typography sx={{ color: 'surface.dark', fontWeight: 700, mb: 0.75, lineHeight: 1.35 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>
      {text}
    </Typography>
  </Box>
);

const FRAME = {
  display: 'block',
  width: '100%',
  border: `1px solid ${LIGHT_GRAY}`,
  borderRadius: '10px',
  backgroundColor: 'common.white',
  boxShadow: '0 1px 2px rgba(26,26,44,.04), 0 18px 40px -16px rgba(26,26,44,.28)',
};

// `media` is a component drawn in the image's place and frame, for a tool
// whose showcase is a diagram rather than a screenshot (OCL Online). It
// renders with the page, so there is no lazy load to reserve space for.
const ProductShowcase = ({ eyebrow, title, image, imageAlt, media: Media, highlights }) => (
  <Box sx={{ backgroundColor: BG_GRAY, borderTop: `1px solid ${LIGHT_GRAY}` }}>
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: { xs: 4.5, md: 6 },
          alignItems: 'center',
        }}
      >
        {Media ? (
          <Box sx={{ ...FRAME, overflow: 'hidden' }}><Media /></Box>
        ) : (
          <Box
            component="img"
            src={image}
            alt={imageAlt}
            loading="lazy"
            // Intrinsic size reserves the 1.4:1 box before the lazy image lands,
            // so the section doesn't collapse and then jump (CLS).
            width={2800}
            height={2000}
            sx={{ ...FRAME, height: 'auto' }}
          />
        )}
        <Box>
          <Typography
            variant="body2"
            sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}
          >
            {eyebrow}
          </Typography>
          <Typography variant="h4" component="h2" sx={{ color: 'surface.dark', mt: 1.25 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3.5 }}>
            {highlights.map(highlight => <Highlight key={highlight.title} {...highlight} />)}
          </Box>
        </Box>
      </Box>
    </Container>
  </Box>
);

export default ProductShowcase;
